<template>
  <div
    v-if="modelValue === true"
    class="cart-drawer-overlay"
    @pointerdown.self="onBackdropPointerDown"
    @pointerup.self="onBackdropPointerUp"
  >
    <aside
      class="cart-drawer"
      role="dialog"
      aria-labelledby="dist-order-title"
      aria-modal="true"
      @pointerdown.stop
    >
      <header class="cart-drawer-header">
        <div>
          <h2 id="dist-order-title" class="mb-0">Revisar pedido</h2>
          <p class="text-muted small mb-0">{{ totalUnits }} unidades · {{ formatPrice(subtotal) }}</p>
        </div>
        <button type="button" class="btn-close btn-close-white" aria-label="Cerrar" @click="close" />
      </header>

      <div class="cart-drawer-body">
        <div
          v-if="showMinPurchaseAlert"
          class="checkout-banner checkout-banner--danger"
          role="alert"
        >
          <i class="fas fa-exclamation-triangle me-1"></i>
          El monto mínimo de compra es {{ formatPrice(minPurchase) }}.
          Te faltan {{ formatPrice(minPurchaseRemaining) }} para llegar al mínimo.
        </div>

        <!-- 1. Productos -->
        <section class="checkout-block">
          <h3 class="checkout-block-title">
            <span class="step-num">1</span>
            Productos
          </h3>
          <div v-if="!lines.length" class="empty-cart text-center py-4 text-muted">
            <i class="fas fa-shopping-basket fa-2x mb-2 d-block opacity-50"></i>
            Agregá productos desde el catálogo
          </div>
          <div v-else class="cart-lines">
            <article v-for="line in lines" :key="line.key" class="cart-line">
              <div class="cart-line-main">
                <strong>{{ line.displayName || line.productName }}</strong>
                <span class="text-muted small">{{ line.quantity }} ud.</span>
                <span
                  v-if="line.offerPercent > 0"
                  class="offer-tag"
                ><i class="fas fa-percent me-1"></i>Oferta −{{ Math.round(line.offerPercent) }}%</span>
              </div>
              <div class="cart-line-actions">
                <div class="qty-mini">
                  <button type="button" class="qty-btn" :disabled="line.quantity <= 1" @click="changeQty(line, -1)">−</button>
                  <span>{{ line.quantity }}</span>
                  <button type="button" class="qty-btn" :disabled="line.quantity >= line.stock" @click="changeQty(line, 1)">+</button>
                </div>
                <div class="line-price-block">
                  <span
                    v-if="line.offerPercent > 0 && line.listPrice"
                    class="line-price-list"
                  >{{ formatPrice(line.listPrice * line.quantity) }}</span>
                  <strong class="line-price">{{ formatPrice(line.price * line.quantity) }}</strong>
                </div>
                <button type="button" class="btn btn-link btn-sm text-danger p-0" @click="$emit('remove-line', line)">
                  Quitar
                </button>
              </div>
            </article>
          </div>

          <!-- Ofertas especiales aplicadas (informativas: ya están en el precio) -->
          <div v-if="offerDiscount > 0" class="offer-note mt-3">
            <i class="fas fa-percent offer-note__icon me-2"></i>
            <span>
              Ofertas especiales aplicadas:
              <strong>ahorrás {{ formatPrice(offerDiscount) }}</strong>
            </span>
          </div>

          <!-- Cupón de descuento -->
          <div v-if="lines.length" class="coupon-block mt-3 pt-3">
            <label class="form-label text-white small mb-2">
              <i class="fas fa-tag me-1"></i>Código de descuento
            </label>
            <div v-if="!appliedCoupon" class="input-group input-group-sm">
              <input
                type="text"
                class="form-control checkout-input"
                placeholder="Ej: VERANO10"
                v-model="couponCodeModel"
                :disabled="couponLoading"
                @keyup.enter="emit('apply-coupon')"
              />
              <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="couponLoading || !couponCodeModel.trim()"
                @click="emit('apply-coupon')"
              >
                <span v-if="couponLoading" class="spinner-border spinner-border-sm"></span>
                <span v-else>Aplicar</span>
              </button>
            </div>
            <div v-else class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span class="text-success small">
                <i class="fas fa-check me-1"></i>
                <strong>{{ appliedCoupon.code }}</strong>
                <template v-if="appliedCoupon.free_shipping"> (envío gratis)</template>
                <template v-else-if="appliedCoupon.discount_percentage">
                  (−{{ appliedCoupon.discount_percentage }}%)
                </template>
                <template v-else>
                  (−{{ formatPrice(appliedCoupon.discount_amount) }})
                </template>
              </span>
              <button type="button" class="btn btn-link btn-sm text-danger p-0" @click="emit('remove-coupon')">
                Quitar cupón
              </button>
            </div>
            <div v-if="couponError" class="text-danger small mt-1">{{ couponError }}</div>
            <div v-if="appliedCoupon && couponDiscount > 0" class="d-flex justify-content-between text-success small mt-2">
              <span>Descuento</span>
              <span>−{{ formatPrice(couponDiscount) }}</span>
            </div>
          </div>
        </section>

        <!-- 2. Datos de entrega (antes del envío para tener CP) -->
        <section class="checkout-block">
          <h3 class="checkout-block-title">
            <span class="step-num">2</span>
            Datos de entrega
          </h3>
          <p class="checkout-block-hint">
            Completá tu dirección y código postal; lo usamos para cotizar el envío.
          </p>
          <div class="checkout-fields row g-3">
            <div class="col-12">
              <label class="form-label">Nombre quien recibe *</label>
              <input
                v-model="checkoutForm.recipient_name"
                class="form-control checkout-input"
                required
                autocomplete="name"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label">Teléfono *</label>
              <input
                v-model="checkoutForm.customer_phone"
                type="tel"
                class="form-control checkout-input"
                required
                autocomplete="tel"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label">Email *</label>
              <input
                v-model="checkoutForm.customer_email"
                type="email"
                class="form-control checkout-input"
                required
                autocomplete="email"
              />
            </div>
            <div class="col-md-8">
              <label class="form-label">Calle *</label>
              <input v-model="checkoutForm.shipping_street" class="form-control checkout-input" required />
            </div>
            <div class="col-md-4">
              <label class="form-label">Número *</label>
              <input v-model="checkoutForm.shipping_number" class="form-control checkout-input" required />
            </div>
            <div class="col-md-6">
              <label class="form-label">Localidad *</label>
              <input v-model="checkoutForm.shipping_city" class="form-control checkout-input" required />
            </div>
            <div class="col-md-6">
              <label class="form-label">Código postal *</label>
              <input
                v-model="checkoutForm.shipping_postal_code"
                class="form-control checkout-input checkout-input--cp"
                maxlength="8"
                inputmode="numeric"
                placeholder="Ej: 5000"
                required
              />
            </div>
            <div class="col-12">
              <label class="form-label">Referencias / piso / depto (opcional)</label>
              <input v-model="checkoutForm.shipping_neighborhood" class="form-control checkout-input" />
            </div>
            <div class="col-12">
              <label class="form-label">Notas del pedido (opcional)</label>
              <textarea v-model="checkoutForm.notes" class="form-control checkout-input" rows="2" />
            </div>
          </div>
        </section>

        <!-- 3. Envío -->
        <section class="checkout-block">
          <h3 class="checkout-block-title">
            <span class="step-num">3</span>
            Envío
          </h3>
          <div v-if="!shippingEnabled" class="checkout-note">
            <i class="fas fa-info-circle me-2"></i>
            La cotización por Correo Argentino no está habilitada. Podés coordinar la entrega en las notas.
          </div>
          <template v-else>
            <div class="payment-methods mb-3">
              <label class="payment-option" :class="{ active: shippingMethod === 'correo_argentino' }">
                <input v-model="shippingMethod" type="radio" value="correo_argentino" class="d-none" />
                <div>
                  <strong>Correo Argentino</strong>
                  <small class="d-block">Cotizá el envío según tu código postal</small>
                </div>
              </label>
              <label class="payment-option" :class="{ active: shippingMethod === 'arrange' }">
                <input v-model="shippingMethod" type="radio" value="arrange" class="d-none" />
                <div>
                  <strong>Acordar envío</strong>
                  <small class="d-block">Coordinamos la modalidad y el costo después de confirmar</small>
                </div>
              </label>
            </div>

            <div v-if="shippingMethod === 'arrange'" class="checkout-note">
              <i class="fas fa-comments me-2"></i>
              El envío no se suma al total. Podés dejar indicaciones en las notas del pedido.
            </div>

            <div v-if="shippingMethod === 'correo_argentino' && originCp" class="origin-chip">
              <i class="fas fa-warehouse me-2"></i>
              Despacho desde CP <strong>{{ originCp }}</strong>
            </div>

            <div v-if="shippingMethod === 'correo_argentino' && !deliveryPostalCode" class="checkout-note checkout-note--warn">
              <i class="fas fa-map-pin me-2"></i>
              Ingresá tu código postal en el paso anterior para ver opciones de envío.
            </div>

            <template v-else-if="shippingMethod === 'correo_argentino'">
              <div v-if="freeShippingInfo?.free_shipping" class="checkout-banner checkout-banner--success">
                <i class="fas fa-check-circle me-1"></i>
                Envío gratis{{ freeShippingInfo.source ? `: ${freeShippingInfo.source}` : '' }}
              </div>
              <div v-else-if="freeShippingInfo?.next_promotion" class="checkout-banner checkout-banner--info">
                Envío gratis desde {{ formatPrice(freeShippingInfo.next_promotion.min_purchase) }}.
                Te faltan {{ formatPrice(freeShippingInfo.next_promotion.amount_remaining) }}.
              </div>

              <div class="shipping-quote-card">
                <ShippingQuote
                  quote-source="wholesale"
                  :quote-items="quoteItems"
                  :free-shipping-info="freeShippingInfo"
                  :external-postal-code="deliveryPostalCode"
                  :start-expanded="true"
                  @rate-selected="onShippingQuoted"
                />
              </div>

              <div v-if="selectedRate" class="shipping-summary">
                <div class="shipping-summary__row">
                  <span>Modalidad</span>
                  <strong>{{ selectedRate.productName }}</strong>
                </div>
                <div class="shipping-summary__row">
                  <span>Entrega</span>
                  <strong>
                    <i :class="isDoorDelivery ? 'fas fa-home me-1' : 'fas fa-building me-1'"></i>
                    {{ isDoorDelivery ? 'A domicilio' : 'Retiro en sucursal' }}
                  </strong>
                </div>
                <div v-if="!isDoorDelivery" class="shipping-summary__row">
                  <span>Sucursal</span>
                  <strong v-if="selectedBranchName" class="text-success">
                    <i class="fas fa-check-circle me-1"></i>{{ selectedBranchName }}
                  </strong>
                  <span v-else class="text-warning small">Elegí sucursal en la cotización</span>
                </div>
                <div class="shipping-summary__row">
                  <span>Plazo</span>
                  <span>{{ selectedRate.deliveryTimeMin }}–{{ selectedRate.deliveryTimeMax }} días hábiles</span>
                </div>
                <div class="shipping-summary__row shipping-summary__row--total">
                  <span>Costo envío</span>
                  <strong>{{ shippingLabel }}</strong>
                </div>
              </div>
            </template>
          </template>
        </section>

        <!-- 4. Pago -->
        <section class="checkout-block">
          <h3 class="checkout-block-title">
            <span class="step-num">4</span>
            Método de pago
          </h3>
          <div class="payment-methods">
            <label
              v-if="mercadoPagoEnabled"
              class="payment-option"
              :class="{ active: paymentMethod === 'mercadopago' }"
            >
              <input v-model="paymentMethod" type="radio" value="mercadopago" class="d-none" />
              <div>
                <strong>Mercado Pago</strong>
                <small class="d-block">Tarjeta, débito o transferencia</small>
              </div>
            </label>
            <label class="payment-option" :class="{ active: paymentMethod === 'cash' }">
              <input v-model="paymentMethod" type="radio" value="cash" class="d-none" />
              <div>
                <strong>Acordar pago</strong>
                <small class="d-block">Coordinamos pago y entrega; podés escribirnos desde Mis pedidos</small>
              </div>
            </label>
          </div>
        </section>

        <!-- Resumen -->
        <section class="checkout-block checkout-block--summary">
          <div class="confirm-summary">
            <div class="summary-line">
              <span>Subtotal productos</span>
              <strong>{{ formatPrice(subtotal) }}</strong>
            </div>
            <div v-if="offerDiscount > 0" class="summary-line summary-line--offer">
              <span><i class="fas fa-percent me-1"></i>Ofertas especiales</span>
              <strong class="text-success">Ahorrás {{ formatPrice(offerDiscount) }}</strong>
            </div>
            <div v-if="couponDiscount > 0" class="summary-line text-success">
              <span>Descuento{{ appliedCoupon?.code ? ` (${appliedCoupon.code})` : '' }}</span>
              <strong>−{{ formatPrice(couponDiscount) }}</strong>
            </div>
            <div class="summary-line">
              <span>Envío</span>
              <strong>{{ shippingLabel }}</strong>
            </div>
            <div class="summary-line total">
              <span>Total estimado</span>
              <strong>{{ formatPrice(grandTotal) }}</strong>
            </div>
          </div>
          <p v-if="submitError" class="text-danger small mt-2 mb-0">{{ submitError }}</p>
        </section>
      </div>

      <footer class="cart-drawer-footer">
        <button type="button" class="btn text-white" style="width: fit-content;" @click="close">
          <i class="fas fa-arrow-left me-1"></i>
        </button>
        <button
          type="button"
          class="btn btn-primary cart-drawer-submit"
          :disabled="submitting || !canSubmitConfirm"
          @click="submitCheckout"
        >
          <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
          <i v-else :class="paymentMethod === 'mercadopago' ? 'fas fa-credit-card' : 'fas fa-check'" class="me-2"></i>
          <span class="cart-drawer-submit__label">
            {{ paymentMethod === 'mercadopago' ? 'Pagar con Mercado Pago' : 'Confirmar pedido' }}
          </span>
          <span class="cart-drawer-submit__total">{{ formatPrice(grandTotal) }}</span>
        </button>
      </footer>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, defineExpose, defineModel } from 'vue'
import ShippingQuote from '@/components/shipping/ShippingQuote.vue'

const CP_STORAGE_KEY = 'wholesale_dest_cp'

const modelValue = defineModel({ type: Boolean, default: false })
const checkoutForm = defineModel('checkoutForm', { type: Object, required: true })
const couponCodeModel = defineModel('couponCode', { type: String, default: '' })

const props = defineProps({
  lines: { type: Array, default: () => [] },
  subtotal: { type: Number, default: 0 },
  totalUnits: { type: Number, default: 0 },
  shippingEnabled: { type: Boolean, default: false },
  originCp: { type: String, default: '' },
  freeShippingInfo: { type: Object, default: null },
  mercadoPagoEnabled: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
  submitError: { type: String, default: '' },
  minPurchase: { type: Number, default: 0 },
  appliedCoupon: { type: Object, default: null },
  couponDiscount: { type: Number, default: 0 },
  offerDiscount: { type: Number, default: 0 },
  couponLoading: { type: Boolean, default: false },
  couponError: { type: String, default: '' },
})

const paymentMethod = defineModel('paymentMethod', { type: String, default: 'cash' })
const shippingMethod = defineModel('shippingMethod', { type: String, default: 'correo_argentino' })

const emit = defineEmits(['remove-line', 'update-qty', 'submit', 'quoted', 'apply-coupon', 'remove-coupon'])

const backdropPointerDown = ref(false)
const selectedRate = ref(null)
const quoteId = ref(null)
const selectedBranchName = ref('')

const deliveryPostalCode = computed(() =>
  String(checkoutForm.value.shipping_postal_code || '').trim()
)

const quoteItems = computed(() =>
  props.lines.map((l) => ({
    product_id: l.productId,
    blister_id: l.blisterId,
    quantity: l.quantity,
  }))
)

const isDoorDelivery = computed(() => {
  const t = selectedRate.value?.deliveredType || selectedRate.value?.deliveryType || ''
  return t === 'D' || t === ''
})

const formatPrice = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v || 0)

const rawShippingCost = computed(() => (selectedRate.value ? Number(selectedRate.value.price) : 0))
const shippingCost = computed(() => (props.freeShippingInfo?.free_shipping ? 0 : rawShippingCost.value))

const minPurchaseRemaining = computed(() =>
  Math.max(0, (props.minPurchase || 0) - (props.subtotal || 0))
)
const showMinPurchaseAlert = computed(
  () => (props.minPurchase || 0) > 0 && (props.subtotal || 0) < props.minPurchase
)
const grandTotal = computed(() => {
  const afterCoupon = Math.max(0, props.subtotal - (props.couponDiscount || 0))
  return afterCoupon + (usesCorreoArgentino.value ? shippingCost.value : 0)
})

const usesCorreoArgentino = computed(
  () => props.shippingEnabled && shippingMethod.value === 'correo_argentino'
)

const shippingLabel = computed(() => {
  if (!usesCorreoArgentino.value) return 'Acordar envío · $0 en este pedido'
  if (props.freeShippingInfo?.free_shipping) return `${formatPrice(0)} (gratis)`
  if (!selectedRate.value) return '—'
  return formatPrice(shippingCost.value)
})

const shippingComplete = computed(() => {
  if (!usesCorreoArgentino.value) return true
  if (!deliveryPostalCode.value || deliveryPostalCode.value.length < 4) return false
  if (!selectedRate.value || !quoteId.value) return false
  if (!isDoorDelivery.value && !checkoutForm.value.shipping_agency_id) return false
  return true
})

const canSubmitConfirm = computed(() => {
  if (!props.lines.length) return false
  if (showMinPurchaseAlert.value) return false
  const f = checkoutForm.value
  if (!f.recipient_name?.trim() || !f.customer_phone?.trim() || !f.customer_email?.trim()) return false
  if (!f.shipping_street?.trim() || !f.shipping_number?.trim() || !f.shipping_city?.trim()) return false
  if (!deliveryPostalCode.value || deliveryPostalCode.value.length < 4) return false
  if (!shippingComplete.value) return false
  return true
})

const close = () => {
  modelValue.value = false
}

/** Evita cerrar al seleccionar texto en un input y soltar fuera del panel */
const onBackdropPointerDown = (e) => {
  if (e.target === e.currentTarget) backdropPointerDown.value = true
}

const onBackdropPointerUp = (e) => {
  if (e.target === e.currentTarget && backdropPointerDown.value) close()
  backdropPointerDown.value = false
}

const changeQty = (line, delta) => {
  emit('update-qty', { key: line.key, quantity: line.quantity + delta })
}

const onShippingQuoted = (payload) => {
  if (!payload || payload.incomplete) {
    selectedRate.value = null
    quoteId.value = null
    selectedBranchName.value = ''
    return
  }
  selectedRate.value = payload.rate
  quoteId.value = payload.quoteId
  if (payload.postalCode) {
    checkoutForm.value.shipping_postal_code = payload.postalCode
    localStorage.setItem(CP_STORAGE_KEY, payload.postalCode)
  }
  if (payload.selectedBranch) {
    checkoutForm.value.shipping_agency_id = payload.selectedBranch.code
    checkoutForm.value.shipping_agency_name = payload.selectedBranch.name
    selectedBranchName.value = payload.selectedBranch.name
  } else {
    checkoutForm.value.shipping_agency_id = ''
    checkoutForm.value.shipping_agency_name = ''
    selectedBranchName.value = ''
  }
  emit('quoted', { quoteId: payload.quoteId, postalCode: payload.postalCode, selectedRate: payload.rate })
}

const submitCheckout = () => {
  if (showMinPurchaseAlert.value) return
  const cp = deliveryPostalCode.value
  if (cp) checkoutForm.value.shipping_postal_code = cp
  checkoutForm.value.shipping_address = [
    checkoutForm.value.shipping_street,
    checkoutForm.value.shipping_number,
    checkoutForm.value.shipping_city,
    cp,
  ]
    .filter(Boolean)
    .join(', ')
  emit('submit')
}

watch(modelValue, (open) => {
  if (open) {
    const saved = localStorage.getItem(CP_STORAGE_KEY)
    if (saved && !checkoutForm.value.shipping_postal_code) {
      checkoutForm.value.shipping_postal_code = saved
    }
  }
})

watch(
  () => props.lines.length,
  (n) => {
    if (!n) {
      selectedRate.value = null
      quoteId.value = null
      selectedBranchName.value = ''
    }
  }
)

watch(
  () => props.mercadoPagoEnabled,
  (enabled) => {
    if (!enabled && paymentMethod.value === 'mercadopago') paymentMethod.value = 'cash'
  },
  { immediate: true }
)

watch(shippingMethod, (method) => {
  if (method !== 'arrange') return
  selectedRate.value = null
  quoteId.value = null
  selectedBranchName.value = ''
  checkoutForm.value.shipping_agency_id = ''
  checkoutForm.value.shipping_agency_name = ''
  emit('quoted', { quoteId: null, selectedRate: null })
})

defineExpose({
  selectedRate,
  quoteId,
})
</script>

<style scoped>
.cart-drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  justify-content: flex-end;
}

.cart-drawer {
  width: min(520px, 100vw);
  height: 100%;
  background: var(--grey-color, #2a2a2a);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.45);
  animation: slideIn 0.25s ease;
}

@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.cart-drawer-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
}

.checkout-block {
  margin-bottom: 1.35rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.checkout-block:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.checkout-block-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 0.65rem;
  color: var(--white-color, #fff);
}

.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 50%;
  background: var(--primary-color, #ffec00);
  color: var(--black-color, #111);
  font-size: 0.72rem;
  font-weight: 800;
  flex-shrink: 0;
}

.checkout-block-hint {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.55);
  margin: -0.25rem 0 0.85rem;
}

.cart-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem 1.5rem;
  -webkit-overflow-scrolling: touch;
}

.checkout-input {
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: var(--white-color, #fff);
  border-radius: 8px;
  font-size: 0.9rem;
}

.checkout-input:focus {
  background: rgba(0, 0, 0, 0.35);
  border-color: var(--primary-color, #ffec00);
  color: var(--white-color, #fff);
  box-shadow: 0 0 0 2px rgba(255, 236, 0, 0.15);
}

.checkout-input--cp {
  font-weight: 600;
  letter-spacing: 0.04em;
}

.checkout-note {
  font-size: 0.85rem;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 0.75rem;
}

.checkout-note--warn {
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.25);
  color: #ffe08a;
}

.checkout-banner {
  font-size: 0.85rem;
  padding: 0.6rem 0.85rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.checkout-banner--success {
  background: rgba(25, 135, 84, 0.15);
  border: 1px solid rgba(25, 135, 84, 0.35);
  color: #8fd9a8;
}

.checkout-banner--info {
  background: rgba(13, 202, 240, 0.1);
  border: 1px solid rgba(13, 202, 240, 0.25);
  color: #9eeaf9;
}

.checkout-banner--danger {
  background: rgba(220, 53, 69, 0.12);
  border: 1px solid rgba(220, 53, 69, 0.4);
  color: #ff9ba6;
}

.shipping-quote-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
}

.shipping-summary {
  margin-top: 0.85rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.88rem;
}

.shipping-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.28rem 0;
}

.shipping-summary__row span:first-child {
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

.shipping-summary__row--total {
  margin-top: 0.35rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.shipping-summary__row--total strong {
  color: var(--primary-color, #ffec00);
}

.cart-line {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.cart-line:last-child {
  border-bottom: none;
}

.cart-line-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.15rem;
}

.cart-line-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
}

.qty-mini {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  padding: 0.15rem 0.35rem;
}

.qty-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1rem;
  line-height: 1;
}

.qty-btn:disabled {
  opacity: 0.35;
}

.line-price {
  font-size: 0.9rem;
  color: var(--white-color, #fff);
}

.offer-tag {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.08rem 0.4rem;
  border-radius: 999px;
  background: rgba(255, 236, 0, 0.16);
  border: 1px solid rgba(255, 236, 0, 0.55);
  color: var(--primary-color, #ffec00);
}

.line-price-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.line-price-list {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);
  text-decoration: line-through;
  white-space: nowrap;
}

.offer-note {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  background: rgba(255, 236, 0, 0.1);
  border: 1px dashed rgba(255, 236, 0, 0.45);
  color: #ffe873;
}

.offer-note strong {
  color: #fff;
}

.summary-line--offer {
  color: #ffe873;
}

.summary-line--offer span {
  display: inline-flex;
  align-items: center;
}

.origin-chip {
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.coupon-block {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.confirm-summary {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 1.1rem;
}

.confirm-summary .summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  font-size: 0.9rem;
}

.confirm-summary .summary-line.total {
  margin-top: 0.5rem;
  padding-top: 0.65rem;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 1rem;
}

.confirm-summary .summary-line.total strong {
  color: var(--primary-color, #ffec00);
  font-size: 1.15rem;
}

.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.payment-option {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: 2px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.payment-option.active {
  border-color: var(--primary-color, #ffec00);
  background: rgba(255, 236, 0, 0.06);
}

.payment-option small {
  color: rgba(255, 255, 255, 0.55);
  margin-top: 0.15rem;
}

.cart-drawer-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.2);
}

.cart-drawer-submit {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.65rem;
  max-width: 100%;
}

.cart-drawer-submit__label {
  white-space: nowrap;
}

.cart-drawer-submit__total {
  font-weight: 700;
  opacity: 0.95;
}

@media (max-width: 480px) {
  .cart-drawer-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .cart-drawer-submit {
    margin-left: 0;
    justify-content: center;
  }
}
</style>
